// Libraries
import React, {FunctionComponent} from 'react';
import {Row, Col, Form, Modal, DatePicker} from 'antd';
import PropTypes from 'prop-types';
import {useAppDispatch} from '../../../store/store';
import moment from 'moment';
import { useSelector } from 'react-redux';

// Store
import {updateContainerShipment} from '../../../store/redux/actions/containerShipmentActions';
import * as allContainerShipmentSelector from '../../../store/redux/selectors/containerShipmentSelectors';

// Types
import { ContainerShipmentType } from '../../../types/containerShipmentTypes';

// Components and Subcomponents
import ButtonComponent from '../../subComponents/ButtonComponent';
import FormItemComponent from '../../subComponents/FormItemComponent';
import InputComponent from '../../subComponents/InputComponent';
import SelectComponent from '../../subComponents/SelectComponent';

// Functions and Constants
import {MODAL_ACTIONS, UPDATE_CONTAINER_SHIPMENT} from '../../../utils/constants';
import {textCleanup, formatDateForForm} from '../../../utils/functions';

type ContainerShipmentFormModalType = {
  modalAction: string | null;
  containerShipment: ContainerShipmentType | null;
  onToggleModal: (visible: boolean) => void;
};
const ContainerShipmentFormModal: FunctionComponent<ContainerShipmentFormModalType> = (
  {
    modalAction, containerShipment, onToggleModal
  }) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const editContainerShipment = modalAction === MODAL_ACTIONS.edit;
  const { shipmentStateParams } = useSelector(allContainerShipmentSelector.containerShipmentParamsSelector);

  const initialFormValues = containerShipment ? {
    ...containerShipment,
    eta: formatDateForForm(containerShipment.eta),
    ata: formatDateForForm(containerShipment.ata),
    ataContecar: formatDateForForm(containerShipment.ataContecar),
    unloadingDeadline: formatDateForForm(containerShipment.unloadingDeadline),
    scheduledUnloadingDate: formatDateForForm(containerShipment.scheduledUnloadingDate),
    unloadingDate: formatDateForForm(containerShipment.unloadingDate),
  } : {};

  const handleSubmit = (values: ContainerShipmentType) => {
    values.containerNumber = textCleanup(values.containerNumber);
    values.ata = values.ata ? moment(values.ata).format('YYYY-MM-DD') : null;
    values.eta = values.eta ? moment(values.eta).format('YYYY-MM-DD') : null;
    values.ataContecar = values.ataContecar ? moment(values.ataContecar).format('YYYY-MM-DD') : null;
    values.unloadingDeadline = values.unloadingDeadline ? moment(values.unloadingDeadline).format('YYYY-MM-DD') : null;
    values.scheduledUnloadingDate = values.scheduledUnloadingDate ? moment(values.scheduledUnloadingDate).format('YYYY-MM-DD') : null;
    values.unloadingDate = values.unloadingDate ? moment(values.unloadingDate).format('YYYY-MM-DD') : null;

    if(editContainerShipment){
      values.shipmentId = containerShipment!.shipmentId;
      dispatch(updateContainerShipment(values, UPDATE_CONTAINER_SHIPMENT.getAll));
    }
    
    onToggleModal(false);
  }

  return (
    <Modal title={editContainerShipment ? 'Editar contenedor' : 'Nuevo contenedor'} width={900}
           open={true} maskClosable closable={false} keyboard={false} destroyOnClose={true}
           footer={[
             <ButtonComponent key={'cancel'} text={'Cerrar'}
                              className={'w-32 h-8 bg-white border border-grey-dkt-300 leading-4 text-grey-dkt-700 ' +
                                'hover:bg-grey-dkt-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-dkt-400'}
                              onClick={() => onToggleModal(false)} />,
             editContainerShipment && <ButtonComponent key={'submit'} text={editContainerShipment ? 'Editar' : 'Crear'}
                              className={'w-32 h-8 text-white bg-blue-dkt-400 hover:bg-blue-dkt-500 focus:outline-none focus:ring-2 ' +
                                'focus:ring-offset-2 focus:ring-blue-dkt-300 after:content-none'}
                              onClick = {() => form.submit()}/>,
           ]}>
      <Form layout="vertical" form={form} initialValues={initialFormValues} onFinish={handleSubmit}>
        <Row gutter={10}>
          <Col md={8} xs={24}>
            <FormItemComponent name={'containerNumber'} label={'Número contenedor'} required={true}
                               child={<InputComponent disabled={editContainerShipment} />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'blnb'} label={'Blnb'} required={true}
                               child={<InputComponent disabled={editContainerShipment} />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'vesselName'} label={'Embarcación'} required={true}
                               child={<InputComponent />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'carrier'} label={'Transportista'} required={true}
                               child={<InputComponent />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'containerSize'} label={'Tamaño contenedor'} required={true}
                               child={<InputComponent />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'arrivalPort'} label={'Puerto de llegada'} required={true}
                               child={<InputComponent />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'eta'} label={'ETA'} required={false}
                                child={
                                    <DatePicker allowClear={true} 
                                                placeholder="Seleccionar fecha"
                                                format="YYYY-MM-DD"
                                                picker="date"
                                                defaultPickerValue={moment()}
                                                className={'w-full h-9 rounded-md border border-grey-dkt-300'}/>
                                  }
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'ata'} label={'ATA'} required={false}
                                child={
                                    <DatePicker allowClear={true} 
                                                placeholder="Seleccionar fecha"
                                                format="YYYY-MM-DD"
                                                picker="date"
                                                defaultPickerValue={moment()}
                                                className={'w-full h-9 rounded-md border border-grey-dkt-300'}/>
                                  }
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'ataContecar'} label={'ATA Contecar'} required={false}
                                child={
                                    <DatePicker allowClear={true} 
                                                placeholder="Seleccionar fecha"
                                                format="YYYY-MM-DD"
                                                picker="date"
                                                defaultPickerValue={moment()}
                                                className={'w-full h-9 rounded-md border border-grey-dkt-300'}/>
                                  }
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'status'} label={'Estado'} required={true}
                               child={
                                <SelectComponent list={shipmentStateParams} />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'unloadingDeadline'} label={'Fecha límite de descarga'} required={false}
                                child={
                                    <DatePicker disabled = {true}
                                                placeholder="Seleccionar fecha"
                                                format="YYYY-MM-DD"
                                                picker="date"
                                                defaultPickerValue={moment()}
                                                className={'w-full h-9 rounded-md border border-grey-dkt-300'}/>
                                  }
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'scheduledUnloadingDate'} label={'Fecha programada de descarga'} required={false}
                                child={
                                    <DatePicker allowClear={true} 
                                                placeholder="Seleccionar fecha"
                                                format="YYYY-MM-DD"
                                                picker="date"
                                                defaultPickerValue={moment()}
                                                className={'w-full h-9 rounded-md border border-grey-dkt-300'}/>
                                  }
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'skuQuantity'} label={'Cantidad de SKUs'} required={true}
                               child={<InputComponent type={'number'} />}
            />
          </Col>
          <Col md={8} xs={24}>
            <FormItemComponent name={'totalCartons'} label={'Total de cajas'} required={true}
                               child={<InputComponent type={'number'} />}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

ContainerShipmentFormModal.propTypes = {
  modalAction: PropTypes.string,
  containerShipment: PropTypes.any,
  onToggleModal: PropTypes.func.isRequired,
};
ContainerShipmentFormModal.defaultProps = {
  modalAction: '',
  containerShipment: null,
  onToggleModal: () => {
    return null;
  }
};

export default ContainerShipmentFormModal;